import React, { useState, FunctionComponent, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { debounce } from 'lodash';
import { Box, makeStyles, Theme } from '@material-ui/core';
import { Form as FormEngine, FormDefinition } from '@mylicenseone/forms';
import { Form, FilterCondition, FormSection, FormField } from '../../store/modules/application/types';
import Loader from '../Loader';
import FilterFieldDisplay from './FilterFieldDisplay';
import { ErrorState } from '../FormDisplay/types';

type FiltersProps = {
    searchForm: Form;
    isLoading: boolean;
    setListFilters: Function;
    selections?: Record<string, string | string[]>;
    handleFilterSelections?: Function;
    setListScrollToTopTrigger?: Function;
};

const getListFilters = (form: Form, formData: Record<string, string | string[]>) => {
    const listFilters: FilterCondition[] = [];
    const formFieldEntries = form.sections.map((section: FormSection) => section.entries)[0];
    formFieldEntries.forEach((filter: FormField) => {
        if (formData[filter.field]) {
            const condition: FilterCondition = {
                value: formData[filter.field],
                operator: filter.fieldMapping?.metadata?.operator || (filter.typeId === 'choice' ? 'in' : 'like'),
                targetPath: filter.fieldMapping?.targetPath?.split('$.')[1] || '',
            };
            // overwrite in case of lessThanEqualDate operator
            if (condition.operator === 'lessThanEqualDate') {
                condition.operator = 'lessThan';
                condition.value = moment(condition.value).add(1, 'day').format('YYYY-MM-DD');
            }
            listFilters.push(condition);
        }
    });
    return listFilters;
};

const updateFormData = (newData: Record<string, any>) => {
    let updatedFormData = {};
    for (const field in newData) {
        if (newData[field].length !== 0) updatedFormData = { ...updatedFormData, [field]: newData[field] };
    }
    return updatedFormData;
};

const Filters: FunctionComponent<FiltersProps> = (props) => {
    const {
        searchForm,
        setListFilters,
        isLoading,
        selections,
        handleFilterSelections,
        setListScrollToTopTrigger: scrollListToTop,
    } = props;

    const classes = makeStyles((theme: Theme) => ({
        filtersContainer: {
            width: '100%',
            maxHeight: 300,
            color: 'gray',
            paddingTop: '10px',
            paddingBottom: '10px',
        },
        clearFiltersBtn: {
            padding: '0px',
            fontWeight: 500,
            marginLeft: 25,
            'text-transform': 'initial',
            cursor: 'pointer',
            color: theme.palette.primary.main,
            marginTop: 22,
        },
    }))();

    const [formData, setFormData] = useState<Record<string, any>>({});
    const [formErrors, setFormErrors] = useState<Record<string, ErrorState>>({});
    const [defaultSelectionsFlag, setDefaultSelectionsFlag] = useState<boolean>(true);
    const formErrorsRef = useRef<Record<string, ErrorState>>({});
    const [formEngine, setFormEngine] = useState<FormEngine>();

    useEffect(() => {
        if (defaultSelectionsFlag) {
            if (!!selections) {
                setFormData(updateFormData(selections));
                setDefaultSelectionsFlag(false);
            }
        }
    }, [defaultSelectionsFlag, selections]);

    const resetFilters = () => {
        setFormData({});
    };

    const handleInputChange = (dataKey: string, value: unknown) => {
        setFormData(
            updateFormData({
                ...formData,
                [dataKey]: value,
            }),
        );
    };
    const debouncedSetFiltersData = useCallback(
        debounce((searchForm, formData) => {
            if (!!searchForm) {
                const newListFilters = getListFilters(searchForm, formData);
                !!scrollListToTop && scrollListToTop(newListFilters);
                setListFilters(newListFilters);
            }
            !!handleFilterSelections && handleFilterSelections(formData);
        }, 300),
        [],
    );
    useEffect(() => {
        if (!!formEngine) {
            const validateFormData = async () => {
                await formEngine?.evaluateApplicationData(formData);
                if (formEngine.applicationHasErrors()) {
                    // only send back filter-state
                    !!handleFilterSelections && handleFilterSelections(formData);
                } else debouncedSetFiltersData(searchForm, formData);
            };
            validateFormData();
        }
    }, [formEngine, formData, debouncedSetFiltersData, handleFilterSelections, searchForm]);

    useEffect(() => {
        if (searchForm) {
            const engine = new FormEngine(searchForm as FormDefinition);
            engine.onValidationChange((field, error, messages) => {
                formErrorsRef.current = {
                    ...formErrorsRef.current,
                    [field]: {
                        error,
                        messages,
                    },
                };
                setFormErrors(formErrorsRef.current);
            });
            setFormEngine(engine);
        } else setFormEngine(undefined);
    }, [searchForm]);

    if (isLoading) return <Loader />;
    return (
        <Box width="100%" display="flex" justifyContent="space-between">
            <Box width="70%" display="flex" flexWrap="wrap" alignItems="center" className={classes.filtersContainer}>
                {!!searchForm &&
                    searchForm.sections.map((section) =>
                        section.entries.map((entry: any) => (
                            <FilterFieldDisplay
                                key={entry.field}
                                formField={entry}
                                value={formData[entry.field]}
                                error={formErrors[entry.field]}
                                handleInputChange={handleInputChange}
                                formData={formData}
                            />
                        )),
                    )}
                <Box display="flex" flexDirection="column">
                    {Object.keys(formData).length > 0 && (
                        <div onClick={resetFilters} className={classes.clearFiltersBtn} color="primary">
                            Clear Filters
                        </div>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Filters;
